import { gql, useApolloClient } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-internal-modules -- see use-favorite-events.ts
import { myEventsCutoff } from '../../routes/events/my-events-cutoff';
import { Store, useSocketFeed } from '../../store';
import type { QuickSwitchEvent, RawQuickSwitchEvent } from './event';
import { isShownQuickSwitchEvent, mapQuickSwitchEvent, QUICK_SWITCH_EVENT_FIELDS, QUICK_SWITCH_FETCH_POLICY, QUICK_SWITCH_LIMIT, recallTotal, rememberTotal } from './event';
import { useQuickSwitchFavoriteEvents } from './use-favorite-events';

const MY_EVENTS = gql`
  query QuickSwitchMyEvents($input: GetEventsInput!) {
    getEvents(input: $input) {
      total
      result {
        ...QuickSwitchEventFields
      }
    }
  }
  ${QUICK_SWITCH_EVENT_FIELDS}
`;

interface QueryResult {
  getEvents?: {
    total?: number | null;
    result: (RawQuickSwitchEvent | null)[];
  } | null;
}

interface QueryVars {
  input: {
    verifier_email?: string;
    end_time_gte: string;
    start_time_lte?: string;
    page: number;
    limit: number;
  };
}

const SCOPE = 'quick-switch:mine';

export const useQuickSwitchMyEvents = (): {
  events: QuickSwitchEvent[];
  loading: boolean;
  total?: number;
} => {
  const client = useApolloClient();
  const { me } = Store.User.useIdentity();
  const favorites = useQuickSwitchFavoriteEvents();
  const email = me?.email;
  const [total, setTotal] = useState(() => recallTotal(SCOPE));

  const fetchMine = useCallback(async (): Promise<RawQuickSwitchEvent[]> => {
    const { data } = await client.query<QueryResult, QueryVars>({
      query: MY_EVENTS,
      variables: {
        input: {
          verifier_email: email,
          end_time_gte: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          start_time_lte: myEventsCutoff().toISO(),
          page: 1,
          limit: QUICK_SWITCH_LIMIT
        }
      },
      fetchPolicy: QUICK_SWITCH_FETCH_POLICY
    });

    rememberTotal(SCOPE, data.getEvents?.total);
    setTotal(data.getEvents?.total ?? undefined);

    return (data.getEvents?.result ?? []).filter(isShownQuickSwitchEvent);
  }, [client, email]);

  /* A newly assigned event is not in the current list, so its id cannot be matched: every batch counts. */
  const mine = useSocketFeed({
    scope: SCOPE,
    cacheKey: `mine:${email ?? ''}`,
    enabled: true,
    fetch: fetchMine
  });

  const events = useMemo(() => {
    const own = mine.rows.map(mapQuickSwitchEvent);
    const seen = new Set(own.map((event) => event.eid));
    const merged = [...own, ...favorites.events.filter((event) => !seen.has(event.eid))];

    // Soonest first, matching the default order of the /events/mine list.
    return merged.sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
  }, [mine.rows, favorites.events]);

  /* Favourites are merged in on top of the assigned events, so the count covers both. */
  const withFavorites =
    total === undefined ? undefined : total + events.filter((event) => !mine.rows.some((row) => row.eid === event.eid)).length;

  return { events, loading: mine.loading || favorites.loading, total: withFavorites };
};
